import React, { FC, useState, useCallback, useEffect, useMemo } from 'react'
import { ApexOptions } from 'apexcharts'
import { format } from 'date-fns'
import { Box, CircularProgress, Grid, IconButton, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import { useUnstableSensorsChartRequest } from 'src/hooks/api'
import { Chart } from 'src/components/ui'
import { AggregatedUnstableSensor } from 'src/api'

const STEP = 30

export const UnstableSensorsChart: FC = () => {
  const [timeFrom, setTimeFrom] = useState<Date>(new Date(new Date().setDate(new Date().getDate() - (STEP - 1))))
  const [timeTo, setTimeTo] = useState<Date>(new Date())
  const [unstableSensorList, setUnstableSensorList] = useState<AggregatedUnstableSensor[]>([])

  const {
    data: aggregatedUnstableSensorList,
    isInitialLoading,
    isRefetching,
    refetch: refetchUnstableSensorList,
  } = useUnstableSensorsChartRequest({
    params: {
      timeFrom: timeFrom.toISOString(),
      timeTo: timeTo.toISOString(),
    },
    options: { enabled: false },
  })

  useEffect(() => {
    setUnstableSensorList(aggregatedUnstableSensorList || [])
  }, [aggregatedUnstableSensorList])

  useEffect(() => {
    if (timeFrom && timeTo) refetchUnstableSensorList()
  }, [timeFrom, timeTo, refetchUnstableSensorList])

  const isLastPeriod = useCallback(() => {
    return timeTo.getDate() === new Date().getDate()
      && timeTo.getMonth() === new Date().getMonth()
      && timeTo.getFullYear() === new Date().getFullYear()
  }, [timeTo])()

  const handleGoBack = () => {
    if (isRefetching) return

    const newTimeFrom = new Date(new Date(timeFrom).setDate(new Date(timeFrom).getDate() - STEP))
    const newTimeTo = new Date(new Date(timeTo).setDate(new Date(timeTo).getDate() - STEP))
    setTimeFrom(newTimeFrom)
    setTimeTo(newTimeTo)
  }

  const handleGoForward = () => {
    if (isLastPeriod || isRefetching) return

    const newTimeFrom = new Date(new Date(timeFrom).setDate(new Date(timeFrom).getDate() + STEP))
    const newTimeTo = new Date(new Date(timeTo).setDate(new Date(timeTo).getDate() + STEP))
    setTimeFrom(newTimeFrom)
    setTimeTo(newTimeTo)
  }

  const resetRange = () => {
    const newTimeFrom = new Date(new Date(new Date().setDate(new Date().getDate() - STEP)))
    const newTimeTo = new Date()
    setTimeFrom(newTimeFrom)
    setTimeTo(newTimeTo)
  }

  const getSensorBarChartData = useCallback(
    (): ApexOptions['series'] => {
      const unstableChartData: number[] = []
      const deadChartData: number[] = []
      for (let i = 0; i < STEP; i++) {
        const shiftedTime = new Date(new Date(timeTo).setUTCDate(new Date(timeTo).getUTCDate() - i))
        const sensorData = unstableSensorList.find((item) => (
          new Date(item.date).getUTCFullYear() === shiftedTime.getUTCFullYear()
          && new Date(item.date).getUTCMonth() === shiftedTime.getUTCMonth()
          && new Date(item.date).getUTCDate() === shiftedTime.getUTCDate()
        ))
        unstableChartData.unshift(sensorData?.numberOfUnstableSensors || 0)
        deadChartData.unshift(sensorData?.numberOfDeadSensors || 0)
      }
      return [
        {
          name: 'Unstable sensors',
          data: unstableChartData,
        },
        {
          name: 'Dead sensors',
          data: deadChartData,
        }
      ]
    },
    [unstableSensorList, timeTo]
  )

  const labels = useMemo((): string[] => {
    const _labels: string[] = []
    for (let i = 0; i < STEP; i++) {
      const shiftedTime = new Date(new Date(timeTo).setUTCDate(new Date(timeTo).getUTCDate() - i))
      _labels.unshift(format(new Date(shiftedTime), 'MMM dd'))
    }
    return _labels
  }, [timeTo])

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: {
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: {
          csv: {
            headerCategory: 'Time',
          },
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: labels,
    },
    yaxis: {
      labels: {
        formatter: (val) => String(parseInt(val.toString(), 10))
      }
    },
    tooltip: {
      y: {
        formatter: (val) => String(parseInt(val.toString(), 10))
      }
    },
  }

  return (
    <Grid container spacing={2} direction='column' wrap="nowrap">
      <Grid item>
        <Typography variant='h6'>Unstable In-Use sensors chart</Typography>
      </Grid>
      <Grid item container alignItems="center">
        <Grid item>
          <IconButton aria-label="go-back" color="primary" onClick={handleGoBack} disabled={isRefetching}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item px={1}>
          <Typography variant="body1">
            {`${format(timeFrom, 'PP')} - ${format(timeTo, 'PP')}`}
          </Typography>
        </Grid>
        <Grid item>
          <IconButton aria-label="go-forward" color="primary" onClick={handleGoForward} disabled={isLastPeriod || isRefetching}>
            <ArrowForwardIcon />
          </IconButton>
        </Grid>
        <Grid item>
          <IconButton aria-label="reset" color="primary" onClick={resetRange} disabled={isLastPeriod || isRefetching}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Grid item container flexDirection="column" spacing={1}>
        <Grid item container>
          <Typography variant="subtitle2" fontSize={16}>Legend</Typography>
        </Grid>
        <Grid item container spacing={2} flexDirection="column">
          <Grid item>
            <Grid item container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="subtitle2">Unstable sensors</Typography>
              </Grid>
            </Grid>
            <Typography variant="body2">Sensors that have less than 24 missed uplinks for a specific date</Typography>
          </Grid>
          <Grid item>
            <Grid item container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="subtitle2">Dead sensors</Typography>
              </Grid>
            </Grid>
            <Typography variant="body2">Sensors that missed 24 uplinks for a specific date</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container minWidth={0}>
        <Box height="400px" ml={-2} width="calc(100% + 16px)" minWidth={0} position="relative">
          {
            isInitialLoading && (
              <>
                <Box
                  position="absolute"
                  width="100%"
                  height="100%"
                  zIndex={1}
                  sx={{
                    background: "grey",
                    opacity: 0.1,
                  }}
                />
                <Box
                  position="absolute"
                  width="100%"
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircularProgress color="primary" size={26} />
                </Box>
              </>
            )
          }
          <Chart options={chartOptions} data={getSensorBarChartData()} />
        </Box>
      </Grid>
    </Grid>
  )
}
